/* eslint-disable react/no-array-index-key */
import {SerializedStyles} from '@emotion/react';
import {
  Autocomplete,
  AutocompleteCloseReason,
  AutocompleteInputChangeReason,
  AutocompleteRenderGroupParams,
  AutocompleteRenderOptionState,
  FilterOptionsState,
  PopperProps,
  TextField,
  Typography,
  createFilterOptions,
  css,
  useTheme,
} from '@mui/material';
import {
  ArrayUtils,
  DlMuiCaretDownIcon,
  DlMuiChip,
  DlMuiDeleteIcon,
  GetStylesFunction,
  StringUtils,
  TranslateUtils,
  isOptionEqualToValue,
  utils,
} from '@wallaby-ui/core';
import classNames from 'classnames';
import React, {JSXElementConstructor, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {defineMessages, IntlShape, useIntl} from 'react-intl';

import lightFilterNodeStyles from './LightFilterNodeStyles.less';
import {FilterConfig} from './LightFormatter';
import {LightInputLabel} from './LightInputLabel';
import {ValidateError} from './LightValidator';
import {FILTER_TYPE_MULTI_SELECT, FILTER_TYPE_SELECT} from 'src/js/lib/filtering';

const I18N = defineMessages({
  LOADING: {
    id: 'hierarchy.children.loading',
    defaultMessage: 'Loading ...',
    description: 'Dropdown label when options are loading',
  },
  NO_OPTIONS: {
    id: 'filter.dropDown.noOptions',
    defaultMessage: 'No matches found',
    description: 'Dropdown label when no options are available',
  },
  PLACEHOLDER: {
    id: 'filter.dropDown.placeholder',
    defaultMessage: 'Select...',
    description: 'placeholder in drop down input',
  },
});

export type Option = {
  id?: string | number;
  name?: string;
  label?: string;
  messageKey?: string;
  disabled?: boolean;
  parentId?: string | number;
  parentName?: string | number;
};
export type NormalizedOption<T = any> = {
  id?: string;
  label?: string;
  disabled?: boolean;
  parentId?: string | number;
  original: T;
};
export type FreeSoloValue = {freeSolo: string};
export function isFreeSolo(ref: any): ref is FreeSoloValue {
  const isObject = typeof ref === 'object' && Boolean(ref); // NOTE: typeof null === 'object';
  return isObject && 'freeSolo' in ref;
}
export type RenderTagCallback = (
  info: {
    optionId: string;
    label: string;
    original: any;
  },
  index: number,
  tagProps: any
) => JSX.Element;
export type LightSelectFilterProps<T = any> = {
  config: FilterConfig;
  optional?: boolean;
  options: Option[];
  value: any;
  onChange?: (newValue: any) => void;
  multi?: boolean;
  isClearable?: boolean;
  disabled?: boolean;
  showChipsInSingleSelect?: boolean;
  freeSolo?: boolean;
  /** for example: `"OTHER - {}"` */
  freeSoloFormat?: string;
  /** for example: `"Add '{}'"` */
  addFreeSoloFormat?: string;
  getFreeSoloDisabled?: (option: FreeSoloValue) => boolean | undefined;
  loadingText?: string;
  noOptionsText?: string;
  placeholderText?: string;
  getOptionLabel?: (option: NormalizedOption<T>) => string;
  loading?: boolean;
  css?: SerializedStyles;
  autocompleteCss?: SerializedStyles;
  chipCss?: (selected: T | FreeSoloValue) => SerializedStyles | undefined;
  isDefaultValue?: boolean;
  error?: ValidateError;
  /** Max number of selected values shown as chips when the input is not focused; the rest collapses into "+N". Set -1 to always show all. */
  limitTags?: number;
  filterOptions?: (options: NormalizedOption<T>[], state: FilterOptionsState<NormalizedOption<T>>) => any[];
  maxWidth?: number | string;
  style?: React.CSSProperties;
  // props to passthrough
  disableListWrap?: boolean;
  isVirtualized?: boolean;
  PopperComponent?: JSXElementConstructor<PopperProps>;
  ListboxComponent?: JSXElementConstructor<React.HTMLAttributes<HTMLElement>>;
  ListboxProps?: any;
  disableCloseOnSelect?: boolean;
  onInputChange?: (event: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => void;
  open?: boolean;
  onOpen?: (event: React.SyntheticEvent) => void;
  onClose?: (event: React.SyntheticEvent, reason: AutocompleteCloseReason) => void;
  renderOption?: (props: any, option: FreeSoloValue | NormalizedOption<T>, state: AutocompleteRenderOptionState, ownerState: any) => void;
  renderTag?: RenderTagCallback;
  renderGroup?: (params: AutocompleteRenderGroupParams) => React.ReactNode;
  /** Rendered after the given option in the flat (non-hierarchy) listbox; use to draw a group divider. */
  getOptionSeparator?: (option: NormalizedOption<T>) => React.ReactNode;
};

const defaultFilterOptions = createFilterOptions<NormalizedOption>();
export type SelectParams = {
  options: Option[];
  getOptionLabel: ((option: NormalizedOption) => string) | undefined;
  freeSolo: boolean | undefined;
  freeSoloFormat: string | undefined;
  addFreeSoloFormat: string | undefined;
  isVirtualized: boolean | undefined;
  chipCss?: LightSelectFilterProps<any>['chipCss'];
  renderTag?: RenderTagCallback;
};
export function getSelectOptions(intl: IntlShape, params: SelectParams | undefined) {
  const {
    options = [],
    getOptionLabel: _getOptionLabel,
    freeSolo,
    freeSoloFormat = '{}',
    addFreeSoloFormat, // default from translations below
    isVirtualized,
    chipCss,
    renderTag,
  } = params ?? {};
  const normalizedOptions: NormalizedOption[] = intl
    ? options.map((option) => ({
        id: String(option.id ?? option.name),
        label: option.messageKey ? utils.TranslateUtils.translate(option.messageKey, intl) : option.label,
        disabled: option.disabled,
        parentId: option.parentId ?? option.parentName,
        original: option,
      }))
    : [];
  const realIsOptionEqualToValue = (option: any, value: any) => {
    return isOptionEqualToValue(option, value, 'id');
  };
  const getOptionLabel = (option: any) => {
    if (isFreeSolo(option)) {
      return formatFreeSoloValue(option.freeSolo, freeSoloFormat);
    }
    if (_getOptionLabel) {
      return _getOptionLabel(option);
    }
    return option?.label ?? '';
  };
  const renderInfos = (selectedValues: any[]) => {
    return selectedValues.map((selectedValue: any) => {
      let optionId = '';
      let label = '?';
      if (isFreeSolo(selectedValue)) {
        optionId = selectedValue.freeSolo;
        label = getOptionLabel(selectedValue);
      } else {
        const option = normalizedOptions.find((v) => realIsOptionEqualToValue(v, selectedValue));
        optionId = option?.id ?? '';
        label = getOptionLabel(option);
      }
      return {optionId, label, original: selectedValue};
    });
  };
  const renderTags = (selectedValues: any[], getTagProps: (state: {index: number}) => any) =>
    renderInfos(selectedValues).map((info, index) => {
      const {optionId, label} = info;
      const tagProps = getTagProps({index});
      return renderTag ? (
        renderTag(info, index, tagProps)
      ) : (
        <DlMuiChip
          {...tagProps}
          css={chipCss && chipCss(info.original)}
          key={`${optionId}-${index}`}
          label={String(label)}
          deleteIcon={<DlMuiDeleteIcon />}
        />
      );
    });
  const renderOption = (props: any, option: FreeSoloValue | NormalizedOption, state: AutocompleteRenderOptionState) => {
    const {key} = props;
    const index = state.index ?? props['data-option-index']; // eslint-disable-line
    let label = getOptionLabel(option);
    if (freeSolo) {
      const isNewFreeSoloOption =
        isFreeSolo(option) && option.freeSolo === stripFreeSoloFormat(state.inputValue, freeSoloFormat) && !state.selected;
      if (isNewFreeSoloOption) {
        const format = addFreeSoloFormat ?? TranslateUtils.translate('global.addFreeSolo', intl);
        label = format.replace('{}', label);
      }
    }
    const className = classNames(props.className, 'light-select-filter-option');
    const virtualProps = isVirtualized ? {component: 'li', noWrap: true} : {};
    return (
      <Typography {...props} key={`${key}-${index}`} variant={'value'} className={className} {...virtualProps}>
        {label}
      </Typography>
    );
  };
  return {normalizedOptions, realIsOptionEqualToValue, getOptionLabel, renderInfos, renderTags, renderOption};
}
export type GetSelectOptions = ReturnType<typeof getSelectOptions>;

function getFreeSoloPrefix(freeSoloFormat: string | undefined) {
  const format = freeSoloFormat ?? '{}';
  const placeholder = '{}';
  const index = format.indexOf(placeholder);

  if (index < 0) return '';
  return format.slice(0, index);
}

function stripFreeSoloFormat(value: string, freeSoloFormat: string | undefined) {
  const prefix = getFreeSoloPrefix(freeSoloFormat);
  if (!prefix) return value;

  let result = value;
  while (result.startsWith(prefix)) {
    result = result.slice(prefix.length);
  }
  return result.trimStart();
}

function formatFreeSoloValue(value: string, freeSoloFormat: string | undefined) {
  const rawValue = stripFreeSoloFormat(value, freeSoloFormat);
  return (freeSoloFormat ?? '{}').replace('{}', rawValue);
}

export const LightSelectFilter = <T,>(props: LightSelectFilterProps<T>) => {
  const {
    config: _config,
    optional,
    options,
    value: valueOrUndefined,
    onChange,
    multi = false,
    isClearable = true,
    disabled = false,
    showChipsInSingleSelect = false,
    freeSolo = false,
    freeSoloFormat,
    addFreeSoloFormat,
    getFreeSoloDisabled = () => false,
    loadingText = I18N.LOADING.id,
    noOptionsText = I18N.NO_OPTIONS.id,
    placeholderText = I18N.PLACEHOLDER.id,
    getOptionLabel: _getOptionLabel,
    loading = false,
    css,
    autocompleteCss,
    chipCss,
    isDefaultValue,
    error,
    limitTags = 1,
    filterOptions,
    maxWidth,
    style,
    disableListWrap,
    isVirtualized,
    PopperComponent,
    ListboxComponent,
    ListboxProps,
    disableCloseOnSelect,
    onInputChange,
    open,
    onOpen,
    onClose,
    renderOption,
    renderTag,
    renderGroup,
    getOptionSeparator,
  } = props;
  // config
  const filterType = multi || showChipsInSingleSelect ? FILTER_TYPE_MULTI_SELECT : FILTER_TYPE_SELECT;
  const config = {
    ..._config,
    ui: {..._config.ui, type: filterType},
  };
  // value
  const value = useMemo(() => {
    if (multi) {
      return valueOrUndefined ?? [];
    }
    if (showChipsInSingleSelect) {
      return valueOrUndefined ? [valueOrUndefined] : [];
    }
    return valueOrUndefined ?? null;
  }, [multi, showChipsInSingleSelect, valueOrUndefined]);

  const [localInputValue, setLocalInputValue] = useState<string | undefined>('');
  const rootAreaMouseDownTarget = useRef<EventTarget | null>(null);

  // CTRL/meta + select keeps the options list open (MUI built-in); keep the typed text too,
  // so the user can keep picking from the same filtered list
  const isKeepOpenSelect = (event: any) => multi && Boolean(event && (event.ctrlKey || event.metaKey));

  const handleLocalInputValue = (event: SyntheticEvent<Element, Event>, value: string, reason: AutocompleteInputChangeReason) => {
    // In multi mode MUI fires input 'reset' after every selection AND again (without an event)
    // when the controlled value updates; ignore them all and clear the text explicitly
    // in handleChange / handleAutocompleteClose, so that CTRL+select keeps the typed filter
    if (reason === 'reset' && multi) return;
    if (typeof onInputChange === 'function') {
      onInputChange(event, value, reason);
    }
    setLocalInputValue(value);
  };

  const handleAutocompleteClose = (event: React.SyntheticEvent, reason: AutocompleteCloseReason) => {
    if (multi) {
      setLocalInputValue('');
    }
    if (onClose) {
      onClose(event, reason);
    }
  };

  const handleChange = useCallback(
    (event, newValue) => {
      if (onChange) {
        if (multi) {
          onChange(newValue.length ? newValue.map((v) => v.id ?? v) : null);
        } else if (showChipsInSingleSelect) {
          const lastValue = newValue.length && newValue[newValue.length - 1];
          onChange(lastValue ? lastValue.id ?? lastValue : null);
        } else {
          onChange(newValue !== null ? newValue.id ?? newValue : null);
        }
      }
      if (!isKeepOpenSelect(event)) {
        setLocalInputValue('');
      }
    },
    [onChange, multi, showChipsInSingleSelect]
  );

  // options
  const intl = useIntl();
  const {
    normalizedOptions,
    realIsOptionEqualToValue,
    getOptionLabel,
    renderTags,
    renderOption: defaultRenderOption,
  } = getSelectOptions(intl, {
    options,
    getOptionLabel: _getOptionLabel,
    freeSolo,
    freeSoloFormat,
    addFreeSoloFormat,
    isVirtualized,
    chipCss,
    renderTag,
  });
  // styles
  const valueToRender = useMemo(() => {
    return multi || showChipsInSingleSelect
      ? value
      : normalizedOptions.find((option) => realIsOptionEqualToValue(option, value)) ?? (value || null);
  }, [multi, showChipsInSingleSelect, value, normalizedOptions, realIsOptionEqualToValue]);

  useEffect(() => {
    const isEmptyObject = freeSolo && value && typeof value === 'object' && Object.keys(value).length === 0;

    if (valueToRender === null || valueToRender === '' || isEmptyObject) {
      setLocalInputValue('');
    }
  }, [valueToRender]);

  const baseRenderOption = renderOption ?? defaultRenderOption;
  const finalRenderOption = getOptionSeparator
    ? (optProps: any, option: FreeSoloValue | NormalizedOption<T>, state: AutocompleteRenderOptionState, ownerState: any) => {
        const node = baseRenderOption(optProps, option, state, ownerState);
        const separator = isFreeSolo(option) ? null : getOptionSeparator(option);
        if (!separator) return node;
        return (
          <React.Fragment key={`${optProps.key}-with-separator`}>
            {node}
            {separator}
          </React.Fragment>
        );
      }
    : baseRenderOption;

  const theme = useTheme();
  const hasValue = multi || showChipsInSingleSelect ? value.length > 0 : value !== null && typeof value !== 'undefined';
  let isOccupied = !isDefaultValue;
  if (typeof isDefaultValue === 'undefined') isOccupied = hasValue;
  if (isOccupied && !hasValue) isOccupied = false;
  const styles = getStyles({theme, params: {css, autocompleteCss, maxWidth}});

  return (
    <span
      className={classNames(lightFilterNodeStyles.dlFilterNodeContent, 'dlFilterNode')}
      css={styles.autocompleteWrapper}
      data-name={config.name}
      style={style}
    >
      <Autocomplete
        disableListWrap={disableListWrap}
        PopperComponent={PopperComponent}
        ListboxComponent={ListboxComponent}
        ListboxProps={ListboxProps}
        data-name={'dl-mui-autocomplete--autocomplete'}
        disabled={disabled}
        popupIcon={<DlMuiCaretDownIcon />}
        multiple={multi || showChipsInSingleSelect}
        limitTags={limitTags}
        disableClearable={!isClearable}
        freeSolo={freeSolo}
        noOptionsText={TranslateUtils.translate(noOptionsText, intl)}
        loadingText={TranslateUtils.translate(loadingText, intl)}
        loading={loading}
        isOptionEqualToValue={(option, value) => realIsOptionEqualToValue(option, value)}
        css={styles.autocomplete}
        options={normalizedOptions as (NormalizedOption<T> | FreeSoloValue)[]}
        fullWidth
        getOptionLabel={getOptionLabel as any}
        getOptionDisabled={(option) => (isFreeSolo(option) ? getFreeSoloDisabled(option) ?? false : option.disabled ?? false)}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
                onMouseDown: (event: React.MouseEvent) => {
                  rootAreaMouseDownTarget.current = event.target;
                },
                onClick: (event: React.MouseEvent) => {
                  // MUI toggles the popup when a click lands on the input's root area; when the
                  // limitTags chips expand on focus, the first click's mouseup can land on a chip
                  // that shifted under the cursor and the retargeted click would immediately close
                  // the just-opened popup — forward the toggle only for clicks that also STARTED
                  // on the root area
                  const mouseDownOnRootArea = rootAreaMouseDownTarget.current === event.currentTarget;
                  rootAreaMouseDownTarget.current = null;
                  if (mouseDownOnRootArea && params.InputProps && typeof (params.InputProps as any).onClick === 'function') {
                    (params.InputProps as any).onClick(event);
                  }
                },
              }}
              InputLabelProps={{
                ...params.InputLabelProps,
                ...(config.ui.label ? {} : {shrink: false}),
              }}
              label={<LightInputLabel text={config.ui.label} optional={optional} intl={intl} />}
              placeholder={TranslateUtils.translate(config.ui.placeholder ?? placeholderText, intl)}
              css={styles.textField}
              error={error?.bool}
              helperText={error?.string}
              data-occupied={isOccupied}
              data-disabled={Boolean(disabled)}
              data-error={Boolean(error?.bool)}
            />
          );
        }}
        renderTags={renderTags}
        renderOption={finalRenderOption}
        renderGroup={renderGroup}
        onChange={handleChange}
        value={valueToRender}
        filterOptions={(options, state) => {
          const filtered: (NormalizedOption<T> | FreeSoloValue)[] = filterOptions
            ? filterOptions(options as NormalizedOption<T>[], state)
            : defaultFilterOptions(options as NormalizedOption<T>[], state);
          const {inputValue} = state;
          if (freeSolo) {
            const arrayValue = ArrayUtils.asArray(value);
            let haveInputValue = false;
            arrayValue.forEach((singleValue) => {
              if (isFreeSolo(singleValue)) {
                if (singleValue.freeSolo.includes(inputValue)) {
                  filtered.push(singleValue);
                  haveInputValue = true;
                }
              }
            });
            if (inputValue && !haveInputValue) {
              filtered.push({freeSolo: stripFreeSoloFormat(inputValue, freeSoloFormat)} as FreeSoloValue);
            }
          }
          return filtered;
        }}
        disableCloseOnSelect={disableCloseOnSelect}
        onInputChange={handleLocalInputValue}
        inputValue={localInputValue}
        open={open}
        onOpen={onOpen}
        onClose={handleAutocompleteClose}
        autoHighlight
      />
    </span>
  );
};

const getStyles: GetStylesFunction = (options) => {
  const maxWidth = options?.params?.maxWidth;
  return {
    // NOTE: we need a fixed width, so that we get the correct size with `flex-wrap: wrap`
    autocompleteWrapper: css(
      `
        display: flex;
        max-width: ${StringUtils.addPx(maxWidth === undefined ? '100%' : maxWidth)};
        width: ${StringUtils.addPx(maxWidth === undefined ? 362 : '100%')};
        padding: 8px 0;
      `,
      options?.params?.css
    ),
    autocomplete: css(
      {
        '& .MuiInputLabel-root': {
          zIndex: 11,
        },
        'div.MuiAutocomplete-tag': {
          maxWidth: '175px',
        },
      },
      options?.params?.autocompleteCss
    ),
    textField: css({
      '& .MuiOutlinedInput-root': {
        paddingRight: '56px !important',
      },
    }),
  };
};
